import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../components/pages/auth/authSlice";
import {
  useGetVapidPublicKeyQuery,
  useSubscribeWebPushMutation,
  useUnsubscribeWebPushMutation,
} from "../app/communicationApiSlice";

/**
 * Converts a base64url VAPID public key string to the Uint8Array expected by
 * PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Registers the browser for web push notifications once the user is logged in.
 * Deregisters when the user logs out.
 *
 * Must be rendered inside an authenticated context so that the RTK Query
 * mutations can attach the Bearer token from the Redux store.
 */
export function useWebPushNotifications() {
  const user = useSelector(selectCurrentUser);
  const isSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window;

  const { data: vapidData } = useGetVapidPublicKeyQuery(undefined, {
    skip: !user || !isSupported,
  });

  const [subscribeWebPush] = useSubscribeWebPushMutation();
  const [unsubscribeWebPush] = useUnsubscribeWebPushMutation();

  // Track the active subscription so we can unsubscribe on logout
  const subscriptionRef = useRef(null);
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!user || !isSupported || !vapidData?.publicKey) return;
    if (subscribedRef.current) return;

    let cancelled = false;

    const register = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        const registration = await navigator.serviceWorker.ready;

        // Check for an existing subscription first (idempotent)
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey),
          });
        }

        if (cancelled) return;

        subscriptionRef.current = subscription;

        const key = subscription.getKey("p256dh");
        const authSecret = subscription.getKey("auth");

        await subscribeWebPush({
          endpoint: subscription.endpoint,
          p256dh: btoa(String.fromCharCode(...new Uint8Array(key))),
          auth: btoa(String.fromCharCode(...new Uint8Array(authSecret))),
          device_name: navigator.userAgent,
        });

        subscribedRef.current = true;
      } catch (_) {
        // Non-critical — web push is best-effort
      }
    };

    register();

    return () => {
      cancelled = true;
    };
  }, [user, isSupported, vapidData, subscribeWebPush]);

  // Deregister when the user logs out
  useEffect(() => {
    if (user) return;
    if (!subscribedRef.current) return;

    const deregister = async () => {
      try {
        const sub = subscriptionRef.current;
        if (sub) {
          await unsubscribeWebPush({ endpoint: sub.endpoint });
          await sub.unsubscribe();
        }
      } catch (_) {
        // best-effort
      } finally {
        subscriptionRef.current = null;
        subscribedRef.current = false;
      }
    };

    deregister();
  }, [user, unsubscribeWebPush]);
}
